import React, { useState, useEffect, useCallback } from 'react';
import { Archive, Calendar, ChevronDown, RefreshCw, Play, Database, ShieldAlert, Clock, Filter } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api } from '../../utils/api';
import { StatCard, SegmentsTable, BreachesTable } from './archive/ArchiveSubComponents';

const ArchiveReportsPanel = ({ drivers = [] }) => {
  const [activeView, setActiveView] = useState('segments');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Segments state
  const [segments, setSegments] = useState([]);
  const [segTotal, setSegTotal] = useState(0);
  const [segPage, setSegPage] = useState(1);
  const [segPages, setSegPages] = useState(1);
  const [segLoading, setSegLoading] = useState(false);

  // Breaches state
  const [breaches, setBreaches] = useState([]);
  const [brTotal, setBrTotal] = useState(0);
  const [brPage, setBrPage] = useState(1);
  const [brPages, setBrPages] = useState(1);
  const [brLoading, setBrLoading] = useState(false);

  // Archived drivers list
  const [archivedDrivers, setArchivedDrivers] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    driverId: '',
    regime: '',
    segmentType: '',
    severity: '',
    breachType: '',
  });
  const [showFilters, setShowFilters] = useState(true);

  // Archive trigger
  const [triggering, setTriggering] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api('/admin/archive/stats');
      setStats(res);
    } catch (err) {
      console.error('Failed to load archive stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadArchivedDrivers = useCallback(async () => {
    try {
      const res = await api('/admin/archive/drivers');
      setArchivedDrivers(res.drivers || []);
    } catch { /* non-critical */ }
  }, []);

  const loadSegments = useCallback(async (page = 1) => {
    setSegLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '50' });
      if (filters.startDate) params.set('start_date', filters.startDate);
      if (filters.endDate) params.set('end_date', filters.endDate);
      if (filters.driverId) params.set('driver_id', filters.driverId);
      if (filters.regime) params.set('regime', filters.regime);
      if (filters.segmentType) params.set('segment_type', filters.segmentType);

      const res = await api(`/admin/archive/segments?${params.toString()}`);
      setSegments(res.segments || []);
      setSegTotal(res.total || 0);
      setSegPage(res.page || 1);
      setSegPages(res.total_pages || 1);
    } catch (err) {
      toast.error('Failed to load archived segments: ' + err.message);
    } finally {
      setSegLoading(false);
    }
  }, [filters]);

  const loadBreaches = useCallback(async (page = 1) => {
    setBrLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '50' });
      if (filters.startDate) params.set('start_date', filters.startDate);
      if (filters.endDate) params.set('end_date', filters.endDate);
      if (filters.driverId) params.set('driver_id', filters.driverId);
      if (filters.regime) params.set('regime', filters.regime);
      if (filters.severity) params.set('severity', filters.severity);
      if (filters.breachType) params.set('breach_type', filters.breachType);

      const res = await api(`/admin/archive/breaches?${params.toString()}`);
      setBreaches(res.breaches || []);
      setBrTotal(res.total || 0);
      setBrPage(res.page || 1);
      setBrPages(res.total_pages || 1);
    } catch (err) {
      toast.error('Failed to load breach evidence: ' + err.message);
    } finally {
      setBrLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadStats();
    loadArchivedDrivers();
  }, [loadStats, loadArchivedDrivers]);

  useEffect(() => {
    if (activeView === 'segments') loadSegments(1);
    else loadBreaches(1);
  }, [activeView, filters, loadSegments, loadBreaches]);

  const triggerArchive = async () => {
    setTriggering(true);
    try {
      const res = await api('/admin/archive/run', { method: 'POST' });
      if (res.status === 'already_running') {
        toast.info('Archive job is already running...');
      } else {
        toast.success('Archive job started. This may take a few minutes...');
      }
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const status = await api('/admin/archive/status');
          if (!status.running) {
            clearInterval(poll);
            setTriggering(false);
            const s = status.last_stats || {};
            if (s.archived > 0 || s.breaches_recorded > 0) {
              toast.success(`Archive done: ${s.archived || 0} archived, ${s.breaches_recorded || 0} breaches`);
            } else {
              toast.info('Archive complete. No new segments to process.');
            }
            loadStats();
            if (activeView === 'segments') loadSegments(1);
            else loadBreaches(1);
            loadArchivedDrivers();
          }
        } catch { /* keep polling */ }
      }, 5000);
      // Safety timeout: stop polling after 10 minutes
      setTimeout(() => { clearInterval(poll); setTriggering(false); }, 600000);
    } catch (err) {
      toast.error('Archive failed: ' + err.message);
      setTriggering(false);
    }
  };

  const applyFilters = () => {
    if (activeView === 'segments') loadSegments(1);
    else loadBreaches(1);
  };

  const clearFilters = () => {
    setFilters({ startDate: '', endDate: '', driverId: '', regime: '', segmentType: '', severity: '', breachType: '' });
  };

  // Merge archived drivers + passed-in drivers for the dropdown
  const driverOptions = (() => {
    const map = new Map();
    for (const d of archivedDrivers) map.set(d.driver_id, d.name);
    for (const d of drivers) {
      if (!map.has(d.driver_id)) {
        map.set(d.driver_id, `${d.users?.first_name || ''} ${d.users?.last_name || ''}`.trim());
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ driver_id: id, name }));
  })();

  return (
    <div className="space-y-6" data-testid="archive-reports-panel">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="Active Segments"
          value={stats?.segments_active ?? '-'}
          icon={Database}
          color="emerald"
          loading={statsLoading}
        />
        <StatCard
          label="Archived Total"
          value={stats?.archived_total ?? '-'}
          icon={Archive}
          color="blue"
          loading={statsLoading}
        />
        <StatCard
          label="Breaches Recorded"
          value={stats?.breaches_total ?? '-'}
          icon={ShieldAlert}
          color="red"
          loading={statsLoading}
        />
        <StatCard
          label="Eligible to Archive"
          value={stats?.segments_eligible_for_archive ?? '-'}
          icon={Clock}
          color="amber"
          loading={statsLoading}
        />
      </div>

      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {/* View Toggle */}
        <div className="flex bg-slate-800 rounded-xl border border-slate-700 p-1">
          <button
            onClick={() => setActiveView('segments')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeView === 'segments'
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="view-segments-tab"
          >
            <Archive className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Archived Segments
          </button>
          <button
            onClick={() => setActiveView('breaches')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeView === 'breaches'
                ? 'bg-red-500/20 text-red-400'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="view-breaches-tab"
          >
            <ShieldAlert className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Breach Evidence
          </button>
        </div>

        {/* Trigger + Refresh */}
        <div className="flex gap-2">
          <button
            onClick={() => { loadStats(); applyFilters(); }}
            className="px-3 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm flex items-center gap-1.5"
            data-testid="refresh-archive-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={triggerArchive}
            disabled={triggering}
            className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 text-sm flex items-center gap-1.5 disabled:opacity-50"
            data-testid="trigger-archive-btn"
          >
            {triggering ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Run Archive Now
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-white font-medium w-full"
          data-testid="toggle-archive-filters"
        >
          <Filter className="w-5 h-5" />
          Filters
          <ChevronDown className={`w-4 h-4 transition ${showFilters ? 'rotate-180' : ''}`} />
          {(filters.startDate || filters.endDate || filters.driverId || filters.regime) && (
            <span className="ml-auto text-xs text-emerald-400">Active</span>
          )}
        </button>

        {showFilters && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  data-testid="archive-filter-start-date"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  data-testid="archive-filter-end-date"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Driver</label>
                <select
                  value={filters.driverId}
                  onChange={(e) => setFilters(f => ({ ...f, driverId: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  data-testid="archive-filter-driver"
                >
                  <option value="">All Drivers</option>
                  {driverOptions.map(d => (
                    <option key={d.driver_id} value={d.driver_id}>{d.name || d.driver_id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Regime</label>
                <select
                  value={filters.regime}
                  onChange={(e) => setFilters(f => ({ ...f, regime: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  data-testid="archive-filter-regime"
                >
                  <option value="">All Regimes</option>
                  <option value="FRHV">FRHV</option>
                  <option value="BUSINESS">Business</option>
                </select>
              </div>
            </div>

            {/* View-specific filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {activeView === 'segments' && (
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Segment Type</label>
                  <select
                    value={filters.segmentType}
                    onChange={(e) => setFilters(f => ({ ...f, segmentType: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                    data-testid="archive-filter-segment-type"
                  >
                    <option value="">All Types</option>
                    <option value="WORK">WORK</option>
                    <option value="REST">REST</option>
                  </select>
                </div>
              )}
              {activeView === 'breaches' && (
                <>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Severity</label>
                    <select
                      value={filters.severity}
                      onChange={(e) => setFilters(f => ({ ...f, severity: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="archive-filter-severity"
                    >
                      <option value="">All Severities</option>
                      <option value="WARNING">Warning</option>
                      <option value="BREACH">Breach</option>
                      <option value="BEYOND_LAW">Beyond Law</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Breach Type</label>
                    <select
                      value={filters.breachType}
                      onChange={(e) => setFilters(f => ({ ...f, breachType: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="archive-filter-breach-type"
                    >
                      <option value="">All Types</option>
                      <option value="CONTINUOUS_WORK_EXCEEDED">Continuous Work Exceeded</option>
                      <option value="WORK_24H_EXCEEDED">Work 24h Exceeded</option>
                      <option value="NIGHT_REST_OVERDUE">Night Rest Overdue</option>
                      <option value="LONG_REST_MISSING">Long Rest Missing</option>
                    </select>
                  </div>
                </>
              )}
              <div className="flex items-end gap-2">
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-slate-400 hover:text-white text-sm"
                  data-testid="clear-archive-filters"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Data Table */}
      {activeView === 'segments' ? (
        <SegmentsTable
          segments={segments}
          total={segTotal}
          page={segPage}
          totalPages={segPages}
          loading={segLoading}
          onPageChange={(p) => loadSegments(p)}
        />
      ) : (
        <BreachesTable
          breaches={breaches}
          total={brTotal}
          page={brPage}
          totalPages={brPages}
          loading={brLoading}
          onPageChange={(p) => loadBreaches(p)}
        />
      )}

      {/* Info Footer */}
      <div className="text-xs text-slate-500 flex items-center gap-2 px-1">
        <Calendar className="w-3.5 h-3.5" />
        Archive runs daily at 03:00 AEST. Segments older than 47 days are flattened and archived. Breach evidence is permanent.
      </div>
    </div>
  );
};

export default ArchiveReportsPanel;
